import React, { useEffect, useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { useAuthStore } from '@/lib/store';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api, mediaUrl, thumbUrl } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldOff, Trash2, HardDrive } from 'lucide-react';
import { toast } from 'sonner';
import { formatBytes } from '@/components/StorageWidget';

export default function AdminPage() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [storage, setStorage] = useState(null);

  const load = async () => {
    try {
      const [u, e, m, s] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/events'),
        api.get('/admin/media-queue'),
        api.get('/admin/storage'),
      ]);
      setUsers(u.data || []); setEvents(e.data || []); setMedia(m.data || []); setStorage(s.data || null);
    } catch (err) { toast.error('Admin load failed'); }
  };
  useEffect(() => { load(); }, []);

  const moderate = async (id, action) => {
    try { await api.post(`/admin/media/${id}/${action}`); load(); toast.success(action); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-page">
      <TopBar title="Admin" subtitle="Moderation & users" user={user} />
      <Tabs defaultValue="media">
        <TabsList className="mb-4 bg-secondary rounded-2xl p-1 w-full overflow-x-auto no-scrollbar flex">
          <TabsTrigger value="media" data-testid="admin-tab-media" className="rounded-xl">Media queue</TabsTrigger>
          <TabsTrigger value="storage" data-testid="admin-tab-storage" className="rounded-xl">Storage</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-xl">Users</TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-tab-events" className="rounded-xl">Events</TabsTrigger>
        </TabsList>
        <TabsContent value="media">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-queue">
            {media.length === 0 && <p className="text-sm text-muted-foreground">No media yet.</p>}
            {media.map((m) => (
              <Card key={m.id} className="rounded-2xl overflow-hidden" data-testid={`admin-media-${m.id}`}>
                <div className="aspect-video bg-secondary relative">
                  {m.is_video ? (
                    <video controls src={mediaUrl(m.id)} className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <img src={thumbUrl(m.id)} alt="" className="absolute inset-0 h-full w-full object-cover" />
                  )}
                  <Badge className="absolute top-2 right-2 rounded-full text-[10px] capitalize" variant="outline">{m.moderation_status}</Badge>
                </div>
                <div className="p-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => moderate(m.id, 'unhide')} data-testid={`admin-approve-${m.id}`} className="rounded-xl text-xs"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => moderate(m.id, 'hide')} data-testid={`admin-hide-${m.id}`} className="rounded-xl text-xs"><ShieldOff className="h-3.5 w-3.5 mr-1" />Hide</Button>
                  <Button size="sm" variant="destructive" onClick={() => moderate(m.id, 'takedown')} data-testid={`admin-remove-${m.id}`} className="rounded-xl text-xs"><Trash2 className="h-3.5 w-3.5 mr-1" />Takedown</Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="storage">
          {storage && (
            <div className="space-y-3" data-testid="admin-storage">
              <Card className="rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><HardDrive className="h-4 w-4" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Total used across platform</div>
                    <div className="font-serif text-2xl font-semibold" data-testid="admin-total-storage">{formatBytes(storage.total_used_bytes)}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-2">Per-event quota: {formatBytes(storage.quota_bytes_per_event)}</div>
              </Card>
              <Card className="rounded-2xl p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Top events by storage</div>
                <div className="space-y-1.5">
                  {(storage.per_event || []).map((p) => (
                    <div key={p.event_id} className="flex items-center justify-between text-sm" data-testid={`admin-storage-event-${p.event_id}`}>
                      <div className="truncate flex-1 mr-3">{p.title} <span className="text-[10px] text-muted-foreground">· {p.count} items</span></div>
                      <div className="font-medium text-xs">{formatBytes(p.used_bytes)}</div>
                    </div>
                  ))}
                  {(storage.per_event || []).length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
                </div>
              </Card>
              <Card className="rounded-2xl p-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Top uploaders</div>
                <div className="space-y-1.5">
                  {(storage.per_user || []).map((p) => (
                    <div key={p.user_id} className="flex items-center justify-between text-sm" data-testid={`admin-storage-user-${p.user_id}`}>
                      <div className="truncate flex-1 mr-3">{p.name} <span className="text-[10px] text-muted-foreground">· {p.count} items</span></div>
                      <div className="font-medium text-xs">{formatBytes(p.used_bytes)}</div>
                    </div>
                  ))}
                  {(storage.per_user || []).length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
                </div>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent value="users">
          <div className="space-y-2" data-testid="admin-users-list">
            {users.map((u) => (
              <Card key={u.id} className="p-3 rounded-2xl flex items-center justify-between" data-testid={`admin-user-${u.id}`}>
                <div>
                  <div className="font-medium text-sm">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </div>
                {u.is_admin ? <Badge>Admin</Badge> : <Badge variant="outline">User</Badge>}
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="events">
          <div className="space-y-2" data-testid="admin-events-list">
            {events.map((e) => (
              <Card key={e.id} className="p-3 rounded-2xl" data-testid={`admin-event-${e.id}`}>
                <div className="font-medium text-sm">{e.title}</div>
                <div className="text-xs text-muted-foreground">{e.type} · {e.start_date} → {e.end_date}</div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
